import { useState, useMemo } from "react";
import { Box, Stack } from "@mui/material";
import { WorkOrderFilters, WorkOrdersTable, WorkOrderStatsCards } from "../components";
import { workOrdersData, getWorkOrderStats } from "../data/workOrdersData";
import type { WorkOrderFilters as WorkOrderFiltersType, WorkOrder } from "../types";

type Order = "asc" | "desc";

const defaultFilters: WorkOrderFiltersType = {
  search: "",
  types: [],
  statuses: [],
  priorities: [],
  zones: [],
  assignedToMe: false,
  slaBreached: false,
  dateRange: { start: null, end: null },
};

export const WorkOrdersList = () => {
  const [filters, setFilters] = useState<WorkOrderFiltersType>(defaultFilters);
  const [orderBy, setOrderBy] = useState<string>("createdAt");
  const [order, setOrder] = useState<Order>("desc");

  const stats = useMemo(() => getWorkOrderStats(), []);

  const filteredWorkOrders = useMemo(() => {
    let result = [...workOrdersData];

    // Apply search filter
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      result = result.filter(
        (wo) =>
          wo.id.toLowerCase().includes(searchLower) ||
          wo.referenceNumber.toLowerCase().includes(searchLower) ||
          wo.description.toLowerCase().includes(searchLower) ||
          wo.location.toLowerCase().includes(searchLower) ||
          wo.zone.toLowerCase().includes(searchLower) ||
          wo.assignedTechnician?.name.toLowerCase().includes(searchLower)
      );
    }

    // Apply type filter
    if (filters.types.length > 0) {
      result = result.filter((wo) => filters.types.includes(wo.type));
    }

    // Apply status filter
    if (filters.statuses.length > 0) {
      result = result.filter((wo) => filters.statuses.includes(wo.status));
    }

    // Apply priority filter
    if (filters.priorities.length > 0) {
      result = result.filter((wo) => filters.priorities.includes(wo.priority));
    }

    // Apply SLA breached filter
    if (filters.slaBreached) {
      result = result.filter((wo) => wo.slaBreached);
    }

    return result;
  }, [filters]);

  const sortedWorkOrders = useMemo(() => {
    const priorityOrder = { critical: 0, high: 1, medium: 2, low: 3 };
    const statusOrder = { open: 0, dispatched: 1, tech_aware: 2, on_site: 3, completed: 4, closed: 5 };

    const comparator = (a: WorkOrder, b: WorkOrder): number => {
      let aValue: string | number;
      let bValue: string | number;

      switch (orderBy) {
        case "id":
          aValue = a.id;
          bValue = b.id;
          break;
        case "priority":
          aValue = priorityOrder[a.priority];
          bValue = priorityOrder[b.priority];
          break;
        case "type":
          aValue = a.type;
          bValue = b.type;
          break;
        case "status":
          aValue = statusOrder[a.status];
          bValue = statusOrder[b.status];
          break;
        case "location":
          aValue = a.location.toLowerCase();
          bValue = b.location.toLowerCase();
          break;
        case "assignedTechnician":
          aValue = a.assignedTechnician?.name.toLowerCase() || "zzz";
          bValue = b.assignedTechnician?.name.toLowerCase() || "zzz";
          break;
        case "zone":
          aValue = a.zone;
          bValue = b.zone;
          break;
        case "createdAt":
        default:
          aValue = new Date(a.createdAt).getTime();
          bValue = new Date(b.createdAt).getTime();
          break;
      }

      if (aValue < bValue) return order === "asc" ? -1 : 1;
      if (aValue > bValue) return order === "asc" ? 1 : -1;
      return 0;
    };

    return [...filteredWorkOrders].sort(comparator);
  }, [filteredWorkOrders, orderBy, order]);

  const handleSort = (property: string) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  return (
    <Box sx={{ p: 3, height: "100%", overflow: "auto" }}>
      <Stack spacing={2}>
        <WorkOrderStatsCards stats={stats} />

        <WorkOrderFilters
          filters={filters}
          onFiltersChange={setFilters}
          totalCount={workOrdersData.length}
          filteredCount={sortedWorkOrders.length}
        />

        <WorkOrdersTable
          workOrders={sortedWorkOrders}
          orderBy={orderBy}
          order={order}
          onSort={handleSort}
        />
      </Stack>
    </Box>
  );
};
